import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

/**
 * Reusable single-input prompt dialog. Replaces window.prompt() while keeping the same flow.
 *
 * Usage (imperative-style helper that mounts on demand):
 *   const name = await openPromptDialog({ title: 'Bookmark name', placeholder: 'My view', defaultValue: 'View 1' });
 *   if (!name) return; // user cancelled
 *
 * Or render <PromptDialog ... /> directly with controlled state.
 */
export function PromptDialog({
  open, onOpenChange,
  title, label, placeholder, defaultValue = '',
  confirmLabel = 'Save', cancelLabel = 'Cancel',
  onConfirm,
  testId = 'prompt-dialog',
}) {
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setValue(defaultValue);
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open, defaultValue]);

  const handleConfirm = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    onConfirm(trimmed);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-sm" data-testid={testId}>
        <DialogHeader>
          <DialogTitle className="font-heading text-base">{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 mt-2">
          {label && <Label className="text-xs text-muted-foreground">{label}</Label>}
          <Input
            ref={inputRef}
            value={value}
            onChange={e => setValue(e.target.value)}
            placeholder={placeholder}
            className="rounded-lg bg-muted/50 border-border/50 text-sm h-9"
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); handleConfirm(); }
              if (e.key === 'Escape') { e.preventDefault(); onOpenChange(false); }
            }}
            data-testid={`${testId}-input`}
          />
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg text-xs h-8" data-testid={`${testId}-cancel`}>
            {cancelLabel}
          </Button>
          <Button onClick={handleConfirm} disabled={!value.trim()} className="rounded-lg text-xs h-8 bg-primary text-primary-foreground" data-testid={`${testId}-confirm`}>
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
